import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Typography,
  Card,
  CardContent,
  Alert,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  ImageList,
  ImageListItem,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  AppBar,
  Toolbar,
  Modal,
  Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../../Context/ApiContext';
import { getAuth } from 'firebase/auth';
import { updateAssessmentStatus } from './UpdateAssessmentStatus';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import CheckIcon from '@mui/icons-material/Check'; // For Accept Button
import CloseIcon from '@mui/icons-material/Close'; // For Reject Button

const AssessmentDetails = () => {
  const { assessmentId } = useParams();
  const { t } = useTranslation();
  const api = useApi();
  const [assessmentData, setAssessmentData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [invitationId, setInvitationId] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [responseTimestamp, setResponseTimestamp] = useState('');
  const [checkpointId, setCheckpointId] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const navigate = useNavigate();

  const location = useLocation();
  const passedInvitationId = location.state?.invitationId;
  const passedCheckpointId = location.state?.checkpointId;

  useEffect(() => {
    let isMounted = true;

    const fetchAssessmentData = async () => {
      setLoading(true);
      try {
        const data = await api.getAssessment(assessmentId);
        if (isMounted) {
          setAssessmentData(data);
          setInvitationId(data.invitation_id || passedInvitationId || '');
          setCheckpointId(data.invitation?.checkpoint_id || passedCheckpointId || '');
          setError('');
        }
      } catch (err) {
        if (isMounted) {
          setError(t('error.fetchFailed'));
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (assessmentId) fetchAssessmentData();

    return () => {
      isMounted = false;
    };
  }, [assessmentId, api, t, passedInvitationId, passedCheckpointId]);

  const handleAccept = async () => {
    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
      setError(t('error.userNotAuthenticated'));
      return;
    }

    const userId = currentUser.uid;

    if (!assessmentData || !invitationId || !checkpointId || !userId) {
      setError(t('error.userIdRequired'));
      return;
    }

    setLoading(true);
    try {
      const additionalData = {
        invitation_id: invitationId,
        checkpoint_id: checkpointId,
        price: assessmentData.item_details.price,
        images: assessmentData.item_details.images || [],
      };

      await updateAssessmentStatus(assessmentId, 'Accepted', additionalData);
      setSuccessMessage(t('success.accepted', { itemName: assessmentData.item_details.name }));
      setResponseTimestamp(new Date().toLocaleString());
      setError('');
      setSnackbarOpen(true);
    } catch (err) {
      setError(t('error.acceptInvitationFailed'));
      console.error('Accept Invitation Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
      setError(t('error.userNotAuthenticated'));
      return;
    }

    const userId = currentUser.uid;

    if (!assessmentData || !invitationId || !checkpointId || !userId) {
      setError(t('error.userIdRequired'));
      return;
    }

    setLoading(true);
    try {
      const additionalData = {
        invitation_id: invitationId,
        checkpoint_id: checkpointId,
        price: assessmentData.item_details.price,
        images: assessmentData.item_details.images || [],
      };

      await updateAssessmentStatus(assessmentId, 'Rejected', additionalData);
      setSuccessMessage(t('success.rejected', { itemName: assessmentData.item_details.name }));
      setResponseTimestamp(new Date().toLocaleString());
      setError('');
      setSnackbarOpen(true);
    } catch (err) {
      setError(t('error.rejectInvitationFailed'));
      console.error('Reject Invitation Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleImageClick = (imgSrc) => {
    setSelectedImage(imgSrc);
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" style={{ height: '100vh' }}>
        <CircularProgress />
      </Grid>
    );
  }

  const isActionable = assessmentData && !['Completed', 'In Progress', 'Accepted', 'Rejected'].includes(assessmentData.status);

  return (
    <div>
      {/* AppBar with Back Button and Title */}
      <AppBar position="static" elevation={0} style={{ background: '#616161' }}> {/* Grey AppBar */}
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" style={{ flexGrow: 1 }}>
            {t('assessmentDetails.title')}
          </Typography>
          {isActionable ? (
            <>
              <Button
                variant="contained"
                startIcon={<CheckIcon />} // Add icon for Accept
                style={{ background: '#4CAF50', color: 'white', borderRadius: '20px', marginRight: '8px', fontWeight: 'bold' }} // Green for Accept
                onClick={handleAccept}
                disabled={loading || successMessage}
              >
                {t('actions.accept')}
              </Button>
              <Button
                variant="contained"
                startIcon={<CloseIcon />} // Add icon for Reject
                style={{ background: '#F44336', color: 'white', borderRadius: '20px', fontWeight: 'bold' }} // Red for Reject
                onClick={handleReject}
                disabled={loading || successMessage}
              >
                {t('actions.reject')}
              </Button>
            </>
          ) : (
            <Typography variant="h6" style={{ marginRight: '16px' }}>
              {t('assessmentDetails.status')}: {assessmentData?.status}
            </Typography>
          )}
        </Toolbar>
      </AppBar>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          severity={successMessage ? 'success' : 'error'}
          icon={successMessage ? <CheckCircleIcon /> : <ErrorIcon />}
        >
          {successMessage || error}
        </Alert>
      </Snackbar>
      
{/* Main Content */}
<Grid container spacing={3} style={{ padding: '16px' }}>
  {/* Assessment Details */}
  <Grid item xs={12} md={6}>
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          <InfoIcon style={{ verticalAlign: 'middle', marginRight: '8px' }} />
          {t('assessmentDetails.itemDetails')}
        </Typography>
        <Divider style={{ marginBottom: '16px' }} />

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">{t('assessmentDetails.itemInfo')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <strong>{t('assessmentDetails.itemName')}:</strong> {assessmentData?.item_details.name}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <strong>{t('assessmentDetails.itemPrice')}:</strong> {assessmentData?.item_details.price}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <strong>{t('assessmentDetails.itemDescription')}:</strong> {assessmentData?.item_details.description}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <strong>{t('assessmentDetails.itemLink')}:</strong>{' '}
                  <a href={assessmentData?.item_details.url} target="_blank" rel="noopener noreferrer">
                    {assessmentData?.item_details.url}
                  </a>
                </Typography>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">{t('assessmentDetails.images')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {assessmentData?.item_details.images && (
              <ImageList cols={3} gap={8}>
                {assessmentData.item_details.images.map((imgSrc, index) => (
                  <ImageListItem key={index} onClick={() => handleImageClick(imgSrc)} style={{ cursor: 'pointer' }}>
                    <img src={imgSrc} alt={`Assessment Image ${index + 1}`} style={{ borderRadius: '8px' }} />
                  </ImageListItem>
                ))}
              </ImageList>
            )}
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  </Grid>

  {/* Buyer and Status Details */}
  <Grid item xs={12} md={6}>
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          <InfoIcon style={{ verticalAlign: 'middle', marginRight: '8px' }} />
          {t('assessmentDetails.buyerAndStatus')}
        </Typography>
        <Divider style={{ marginBottom: '16px' }} />

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">{t('assessmentDetails.buyerInfo')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <strong>{t('assessmentDetails.buyerEmail')}:</strong> {assessmentData?.buyer_email}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <strong>{t('assessmentDetails.invitationId')}:</strong> {invitationId}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <strong>{t('assessmentDetails.checkpointId')}:</strong> {checkpointId || t('messages.noCheckpointId')}
                </Typography>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">{t('assessmentDetails.statusInfo')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <strong>{t('assessmentDetails.status')}:</strong> {assessmentData?.status || 'Not available'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <strong>{t('assessmentDetails.createdAt')}:</strong> {assessmentData?.created_at}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <strong>{t('assessmentDetails.acceptedAt')}:</strong>{' '}
                  {assessmentData?.accepted_at ? new Date(assessmentData.accepted_at).toLocaleString() : t('assessmentDetails.notYetAccepted')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <strong>{t('assessmentDetails.lastUpdated')}:</strong> {assessmentData?.updated_at}
                </Typography>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  </Grid>
</Grid>


      {/* Modal for Image Preview */}
      <Modal open={Boolean(selectedImage)} onClose={handleCloseModal}>
        <Box
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'white',
            padding: '16px',
            borderRadius: '8px',
            outline: 'none',
          }}
        >
          <img src={selectedImage} alt="Preview" style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: '8px' }} />
        </Box>
      </Modal>
    </div>
  );
};

export default AssessmentDetails;